import { useCallback, useEffect, useMemo, useState } from 'react';
import { FORECAST_ROUND_THRESHOLD, roundHoursWithThreshold, roundQuantityWithThreshold } from '../utils/forecastMath.js';
import { isBeforeDeviceCurrentMinute, PAST_FORECAST_TIME_ERROR } from '../utils/forecastTimePermissions.js';

const selectionToArray = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (value instanceof Set) {
    return Array.from(value).filter(Boolean);
  }
  if (typeof value[Symbol.iterator] === 'function') {
    return Array.from(value).filter(Boolean);
  }
  return [];
};

const parseForecastTime = (value, fallbackDate) => {
  if (value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  if (fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())) {
    return fallbackDate;
  }
  return null;
};

const generateSnapshotUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11)
    .replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & 15)) >> (c / 4))
    .toString(16);
};

const resolveProcessForecastKind = (process) => {
  if (!process) {
    return null;
  }
  if (process.proceso_es_azucar_glass) {
    return 'azucar_glass';
  }
  if (process.proceso_es_warmer) {
    return 'warmer';
  }
  const raw = String(process.proceso_tipo || 'articulos').toLowerCase();
  if (raw.includes('recet')) {
    return 'recetas';
  }
  if (raw.includes('art')) {
    return 'articulos';
  }
  return 'articulos';
};

const determineEndpoint = (selectedProcesos, processesMeta) => {
  if (!selectedProcesos.length) {
    return { ok: false, code: 'no_selection', error: 'Seleccione al menos un proceso.' };
  }
  const processTypes = new Set(
    selectedProcesos
      .map((name) => processesMeta.find((process) => process.proceso === name))
      .filter(Boolean)
      .map((process) => resolveProcessForecastKind(process))
  );
  if (processTypes.size === 0) {
    return { ok: false, code: 'invalid_selection', error: 'No se pudo validar la selección de procesos.' };
  }
  if (processTypes.size !== 1) {
    return {
      ok: false,
      code: 'mixed_types',
      error: 'No se pueden combinar diferentes tipos de pronóstico (Artículos, Recetas, Azúcar Glass y Warmer).'
    };
  }
  if (processTypes.has('recetas') || processTypes.has('warmer')) {
    return { ok: true, url: '/api/get_recipe_forecast' };
  }
  return { ok: true, url: '/api/get_forecast' };
};

export const useForecastGeneration = ({
  restaurant,
  processesSelection,
  weeksSelection,
  weeksError,
  canGenerate,
  simulationTime,
  isManualTime,
  hoursOverride,
  onHoursOverrideChange,
  processesMeta,
  onStart,
  onSuccess,
  onError,
  onWarning,
  warmerMode = false,
  azucarGlassMode = false,
  isSuperAdmin = false
}) => {
  const [status, setStatus] = useState({ loading: false, success: false, error: null });
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const procesosArray = useMemo(() => selectionToArray(processesSelection), [processesSelection]);
  const weeksArray = useMemo(() => selectionToArray(weeksSelection), [weeksSelection]);

  useEffect(() => {
    if (!status.success) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setStatus((prev) => (prev.success ? { ...prev, success: false } : prev));
    }, 3200);
    return () => clearTimeout(timeoutId);
  }, [status.success]);

  const openConfirmation = useCallback(() => {
    if (!canGenerate) {
      setStatus((prev) => ({
        ...prev,
        error: 'Configura los parámetros antes de generar el pronóstico.'
      }));
      return;
    }
    const endpoint = determineEndpoint(procesosArray, processesMeta || []);
    if (!endpoint.ok) {
      const message = endpoint.error || 'No se pudo validar la selección de procesos.';
      setStatus((prev) => ({ ...prev, error: message }));
      if (endpoint.code === 'mixed_types' && typeof onWarning === 'function') {
        onWarning(message);
      }
      return;
    }
    setConfirmationOpen(true);
    setStatus((prev) => ({ ...prev, error: null }));
  }, [canGenerate, procesosArray, processesMeta, onWarning]);

  const closeConfirmation = useCallback(() => {
    setConfirmationOpen(false);
  }, []);

  const confirmGeneration = useCallback(async ({ forecastTime, mode, hoursValue }) => {
    const effectiveMode = warmerMode ? 'lifespan' : (azucarGlassMode ? 'hours' : mode);
    if (!canGenerate) {
      setStatus({
        loading: false,
        success: false,
        error: 'Configura los parámetros antes de generar el pronóstico.'
      });
      return;
    }
    if (!restaurant) {
      setStatus({
        loading: false,
        success: false,
        error: 'Selecciona un restaurante válido.'
      });
      return;
    }
    if (weeksError) {
      setStatus({
        loading: false,
        success: false,
        error: weeksError
      });
      return;
    }
    if (!procesosArray.length || !weeksArray.length) {
      setStatus({
        loading: false,
        success: false,
        error: 'Completa la selección de procesos y semanas.'
      });
      return;
    }

    let requestedHoursOverride = null;
    const maxHours = azucarGlassMode ? 24 : 12;
    if (effectiveMode === 'hours') {
      const fallbackValue = typeof hoursOverride === 'number' ? String(hoursOverride) : '';
      const resolvedInput = hoursValue === '' || hoursValue === null || hoursValue === undefined
        ? fallbackValue
        : hoursValue;
      const parsed = roundHoursWithThreshold(parseInt(resolvedInput, 10), FORECAST_ROUND_THRESHOLD, 1);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > maxHours) {
        setStatus({
          loading: false,
          success: false,
          error: azucarGlassMode
            ? 'Ingresa un valor de horas válido entre 1 y 24.'
            : 'Ingresa un valor de horas válido entre 1 y 12.'
        });
        return;
      }
      requestedHoursOverride = parsed;
    } else if (effectiveMode === 'lifespan') {
      requestedHoursOverride = null;
    }

    if (typeof onHoursOverrideChange === 'function') {
      onHoursOverrideChange(warmerMode ? null : requestedHoursOverride);
    }

    const finalHoursOverride = warmerMode
      ? null
      : (requestedHoursOverride !== null
        ? requestedHoursOverride
        : (typeof hoursOverride === 'number' ? Math.max(1, Math.min(maxHours, hoursOverride)) : null));

    const finalSimulationTime = parseForecastTime(forecastTime, simulationTime);
    if (!finalSimulationTime) {
      setStatus({
        loading: false,
        success: false,
        error: 'Selecciona una fecha y hora válidas.'
      });
      return;
    }
    if (!isSuperAdmin && isBeforeDeviceCurrentMinute(finalSimulationTime)) {
      setStatus({
        loading: false,
        success: false,
        error: PAST_FORECAST_TIME_ERROR
      });
      if (typeof onWarning === 'function') {
        onWarning(PAST_FORECAST_TIME_ERROR);
      }
      return;
    }

    const endpoint = determineEndpoint(procesosArray, processesMeta || []);
    if (!endpoint.ok) {
      const message = endpoint.error || 'No se pudo determinar el endpoint.';
      setStatus({
        loading: false,
        success: false,
        error: message
      });
      if (endpoint.code === 'mixed_types' && typeof onWarning === 'function') {
        onWarning(message);
      }
      return;
    }

    const payload = {
      sucursal: restaurant,
      selected_procesos: procesosArray,
      selected_weeks: weeksArray,
      is_manual_time: Boolean(effectiveMode === 'hours' || forecastTime || isManualTime),
      simulation_time: finalSimulationTime.toISOString(),
      modificadores: { clima: 0, promo: 0, evento: 0 },
      snapshot_uuid: generateSnapshotUUID(),
      hours_override: finalHoursOverride
    };

    setConfirmationOpen(false);
    setStatus({ loading: true, success: false, error: null });
    if (typeof onStart === 'function') {
      onStart(payload);
    }

    try {
      const response = await fetch(endpoint.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        let message = `Error ${response.status}`;
        try {
          const errPayload = await response.json();
          if (errPayload?.error) {
            message = errPayload.error;
          }
        } catch (parseError) {
          // ignore parse error
        }
        throw new Error(message);
      }
      const data = await response.json();
      if (typeof onSuccess === 'function') {
        onSuccess({ payload, data });
      }
      setStatus({ loading: false, success: true, error: null });
    } catch (error) {
      const message = error?.message || 'Error al generar el pronóstico.';
      if (typeof onError === 'function') {
        onError(message);
      }
      setStatus({ loading: false, success: false, error: message });
    }
  }, [
    canGenerate,
    restaurant,
    weeksError,
    procesosArray,
    weeksArray,
    hoursOverride,
    simulationTime,
    isManualTime,
    processesMeta,
    onHoursOverrideChange,
    onStart,
    onSuccess,
    onError,
    onWarning,
    warmerMode,
    azucarGlassMode,
    isSuperAdmin
  ]);

  return {
    status,
    confirmationOpen,
    openConfirmation,
    closeConfirmation,
    confirmGeneration
  };
};
